import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Trash2, Tag, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface TagReport {
  tag_name: string;
  report_count: number;
  book_count: number;
  user_count: number;
  recent_reports: Array<{
    id: string;
    reporter_username: string;
    category: string;
    details: string;
    created_at: string;
  }>;
}

export function TagModerationPanel() {
  const queryClient = useQueryClient();
  const [tagToDelete, setTagToDelete] = useState<string | null>(null);

  // Fetch reported tags
  const { data: reportedTags = [], isLoading } = useQuery({
    queryKey: ["admin-reported-tags"],
    queryFn: async () => {
      // Get all tag reports
      const { data: reports, error: reportsError } = await supabase
        .from("content_reports")
        .select(
          `
          id,
          content_id,
          reporter_id,
          report_category,
          additional_details,
          created_at,
          profiles!content_reports_reporter_id_fkey (username)
        `
        )
        .eq("content_type", "tag")
        .eq("status", "pending")
        .order("created_at", { ascending: false });

      if (reportsError) throw reportsError;

      // Group by tag
      const tagReportsMap = new Map<string, TagReport>();

      for (const report of reports || []) {
        const tagName = report.content_id;
        if (!tagReportsMap.has(tagName)) {
          // Get tag stats
          const { data: tagData } = await supabase
            .from("book_tags")
            .select("book_id, user_id")
            .eq("tag_name", tagName);

          const bookCount = new Set(tagData?.map((t) => t.book_id) || []).size;
          const userCount = new Set(tagData?.map((t) => t.user_id) || []).size;

          tagReportsMap.set(tagName, {
            tag_name: tagName,
            report_count: 0,
            book_count: bookCount,
            user_count: userCount,
            recent_reports: [],
          });
        }

        const tagReport = tagReportsMap.get(tagName)!;
        tagReport.report_count++;
        tagReport.recent_reports.push({
          id: report.id,
          reporter_username: (report.profiles as any)?.username || "Unknown",
          category: report.report_category,
          details: report.additional_details || "",
          created_at: report.created_at,
        });
      }

      return Array.from(tagReportsMap.values()).sort(
        (a, b) => b.report_count - a.report_count
      );
    },
  });

  // Delete tag mutation
  const deleteTag = useMutation({
    mutationFn: async (tagName: string) => {
      // Delete all instances of this tag from book_tags
      const { error: deleteError } = await supabase
        .from("book_tags")
        .delete()
        .eq("tag_name", tagName);

      if (deleteError) throw deleteError;

      // Mark all reports as resolved
      const { error: updateError } = await supabase
        .from("content_reports")
        .update({ status: "resolved", reviewed_at: new Date().toISOString() })
        .eq("content_type", "tag")
        .eq("content_id", tagName);

      if (updateError) throw updateError;

      // Remove from user_books.tags arrays
      const { data: affectedBooks } = await supabase
        .from("user_books")
        .select("id, tags")
        .contains("tags", [tagName]);

      if (affectedBooks && affectedBooks.length > 0) {
        for (const book of affectedBooks) {
          const updatedTags = (book.tags || []).filter((t: string) => t !== tagName);
          await supabase
            .from("user_books")
            .update({ tags: updatedTags })
            .eq("id", book.id);
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-reported-tags"] });
      toast.success("Tag deleted successfully");
      setTagToDelete(null);
    },
    onError: (error) => {
      console.error("Error deleting tag:", error);
      toast.error("Failed to delete tag");
    },
  });

  // Dismiss reports mutation
  const dismissReports = useMutation({
    mutationFn: async (tagName: string) => {
      const { error } = await supabase
        .from("content_reports")
        .update({ status: "dismissed", reviewed_at: new Date().toISOString() })
        .eq("content_type", "tag")
        .eq("content_id", tagName);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-reported-tags"] });
      toast.success("Reports dismissed");
    },
    onError: (error) => {
      console.error("Error dismissing reports:", error);
      toast.error("Failed to dismiss reports");
    },
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {reportedTags.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Tag className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No reported tags to review</p>
            </CardContent>
          </Card>
        ) : (
          reportedTags.map((tag) => (
            <Card key={tag.tag_name} className="border-destructive/50">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 text-destructive" />
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Badge variant="destructive">{tag.tag_name}</Badge>
                        <span className="text-sm font-normal text-muted-foreground">
                          {tag.report_count} {tag.report_count === 1 ? "report" : "reports"}
                        </span>
                      </CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">
                        Used on {tag.book_count} books by {tag.user_count} readers
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => dismissReports.mutate(tag.tag_name)}
                      disabled={dismissReports.isPending}
                    >
                      Dismiss
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setTagToDelete(tag.tag_name)}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Tag
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm">Recent Reports:</h4>
                  {tag.recent_reports.slice(0, 3).map((report) => (
                    <div key={report.id} className="bg-muted p-3 rounded-md text-sm">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium">@{report.reporter_username}</span>
                        <Badge variant="outline" className="text-xs">
                          {report.category}
                        </Badge>
                      </div>
                      {report.details && (
                        <p className="text-muted-foreground text-xs">{report.details}</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(report.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                  {tag.report_count > 3 && (
                    <p className="text-xs text-muted-foreground">
                      +{tag.report_count - 3} more reports
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!tagToDelete} onOpenChange={() => setTagToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete tag permanently?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the tag "{tagToDelete}" from all books and dismiss all reports.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => tagToDelete && deleteTag.mutate(tagToDelete)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete Tag
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
